import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Camera, ChevronDown, ExternalLink, KeyRound, LogOut, Settings, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { identityApi } from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/design-system/Button';
import { Dialog, Menu, MenuItem } from '@/design-system/Overlay';
import { ACCEPTED_PHOTO_TYPES, checkPhotoFile } from '@/features/employees/PhotoPicker';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { cn } from '@/lib/cn';

/** Microsoft's own page for changing an Entra ID password. */
const CHANGE_PASSWORD_URL = 'https://mysignins.microsoft.com/security-info/password/change';

/** Up to two initials: "dev-admin" becomes DA, "Priya Sharma" becomes PS. */
function initials(name: string): string {
  const letters = name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => (part[0] ?? '').toUpperCase())
    .join('');
  return letters || '?';
}

/**
 * A profile photo as a small square JPEG data URL.
 *
 * Kept in the account's preferences document rather than as an uploaded file: it is only ever
 * shown to its owner, and at 256px it is a few tens of KB - well inside the document's cap.
 * ponytail: preferences-document storage; move to the employee photo store if others should see it.
 */
async function toAvatarDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  canvas
    .getContext('2d')
    ?.drawImage(
      bitmap,
      (bitmap.width - side) / 2,
      (bitmap.height - side) / 2,
      side,
      side,
      0,
      0,
      256,
      256,
    );
  bitmap.close();
  return canvas.toDataURL('image/jpeg', 0.82);
}

function Avatar({ name, photo, className }: { name: string; photo?: string; className?: string }) {
  return photo ? (
    <img src={photo} alt="" className={cn('shrink-0 rounded-full object-cover', className)} />
  ) : (
    <span
      aria-hidden
      className={cn(
        'brand-mark grid shrink-0 place-items-center rounded-full font-semibold text-white',
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}

/**
 * The signed-in person, in the top bar: photo and name, opening their profile - designation,
 * manager, photo and password.
 */
export function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { document, setSection } = useUserPreferences();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const profile = useQuery({
    queryKey: queryKeys.profile,
    queryFn: identityApi.profile,
    staleTime: 5 * 60_000,
  });

  const name = user?.displayName ?? user?.username ?? 'Signed in';
  const stored = document.profile as { photo?: string } | undefined;
  const photo = typeof stored?.photo === 'string' ? stored.photo : undefined;
  const designation =
    profile.data?.jobTitle || profile.data?.roles?.join(', ') || (profile.isLoading ? '…' : '—');

  const onPhotoPicked = async (file: File | undefined) => {
    if (!file) return;
    const problem = checkPhotoFile(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    try {
      setSection('profile', { ...stored, photo: await toAvatarDataUrl(file) });
      toast.success('Profile photo updated');
    } catch {
      toast.error('That image could not be read. Try a JPEG or PNG.');
    }
  };

  return (
    <>
      <Menu
        label="Your profile"
        panelClassName="w-80"
        triggerClassName="inline-flex h-9 items-center gap-2 rounded-lg px-1.5 text-small text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
        trigger={
          <>
            <Avatar name={name} photo={photo} className="size-7 text-[0.6875rem]" />
            <span className="hidden max-w-40 truncate sm:block">{name}</span>
            <ChevronDown aria-hidden className="size-3.5 opacity-60" />
          </>
        }
      >
        {(close) => (
          <div>
            <div className="flex items-center gap-3 px-2.5 pt-2 pb-3">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="group relative rounded-full"
                aria-label="Change profile photo"
                title="Change profile photo"
              >
                <Avatar name={name} photo={photo} className="size-14 text-base" />
                <span className="absolute inset-0 grid place-items-center rounded-full bg-black/45 text-white opacity-0 transition-opacity group-hover:opacity-100">
                  <Camera className="size-5" />
                </span>
              </button>
              <div className="min-w-0">
                <p className="truncate text-body font-semibold text-text-primary">{name}</p>
                <p className="truncate text-small text-text-secondary">{designation}</p>
                {profile.data?.email && (
                  <p className="truncate text-caption text-text-muted">{profile.data.email}</p>
                )}
              </div>
            </div>

            <dl className="mx-2.5 mb-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg bg-surface-sunken px-3 py-2 text-caption">
              <dt className="text-text-muted">Manager</dt>
              <dd className="truncate text-text-primary">
                {profile.data?.managerName ?? (profile.data?.employeeId ? 'Not recorded' : '—')}
              </dd>
              <dt className="text-text-muted">Username</dt>
              <dd className="truncate text-text-primary">{profile.data?.username ?? user?.username}</dd>
            </dl>

            <div className="border-t border-[var(--rule)] pt-1">
              <MenuItem
                icon={<Camera />}
                onClick={() => {
                  close();
                  fileRef.current?.click();
                }}
              >
                {photo ? 'Change profile photo' : 'Add profile photo'}
              </MenuItem>
              {photo && (
                <MenuItem
                  icon={<Trash2 />}
                  onClick={() => {
                    close();
                    setSection('profile', { ...stored, photo: undefined });
                    toast.success('Profile photo removed');
                  }}
                >
                  Remove photo
                </MenuItem>
              )}
              <MenuItem
                icon={<KeyRound />}
                onClick={() => {
                  close();
                  setPasswordOpen(true);
                }}
              >
                Change password
              </MenuItem>
              <MenuItem
                icon={<Settings />}
                onClick={() => {
                  close();
                  navigate('/settings');
                }}
              >
                Settings
              </MenuItem>
              <MenuItem
                icon={<LogOut />}
                onClick={() => {
                  close();
                  signOut();
                }}
              >
                Sign out
              </MenuItem>
            </div>
          </div>
        )}
      </Menu>

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED_PHOTO_TYPES.join(',')}
        className="hidden"
        onChange={(event) => {
          void onPhotoPicked(event.target.files?.[0]);
          event.target.value = '';
        }}
      />

      <Dialog
        open={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        title="Change password"
        description="Your password belongs to your organisation's Microsoft sign-in, not to SmartAsset360."
        footer={
          <>
            <Button variant="ghost" onClick={() => setPasswordOpen(false)}>
              Close
            </Button>
            <a
              href={CHANGE_PASSWORD_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => setPasswordOpen(false)}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-brand px-3.5 text-small font-medium text-white hover:bg-brand-hover"
            >
              Change it with Microsoft <ExternalLink className="size-4" />
            </a>
          </>
        }
      >
        <p className="text-body text-text-secondary">
          Changing it there changes it everywhere you sign in with your work account, including
          here. Development accounts on a local installation sign in without a password.
        </p>
      </Dialog>
    </>
  );
}
